import React from 'react';
import { connect } from 'react-redux';
import ProgressBar from 'components/ProgressBar';
import Nav from 'components/Nav';
import SubNav from 'components/SubNav/SubNav';
import NotificationBar from 'components/Notification/NotificationBar';
import _ from 'lodash';
import find from 'lodash/find';
import BillingReminder from 'components/BillingReminder';
import MwebSwitchBanner from 'components/MwebSwitchBanner';
import withFeatureFlags from 'hocs/withFeatureFlags';
import classNames from 'classnames';
import { setResponsiveMode, setFullSiteMeta } from 'utils/fullSite';
import { browserHistory } from 'react-router';
import MwebFullSiteSwitchFtue from 'components/MwebFullSiteSwitchFtue';
import {
  FTUE_MWEB_FULL_SITE_SWITCH,
  FTUE_MWEB_WELCOME_MODAL,
} from 'reducers/ftue/ftueKeys';
import { fetchExperiences, completeExperience } from 'reducers/ftue/actions';
import { switchToFullSite as switchToFullSiteClevertap } from 'clevertap/mWebFullSiteSwitch';
import debounce from 'lodash/debounce';
import styles from './LayoutNotLoggedIn.css';
import isExperienceComplete from '../../../selectors/isExperienceComplete';

class LayoutNotLoggedIn extends React.Component {
  constructor(props) {
    super(props);

    // When invoked successively, meta tag will be set wrongly.
    // That is because of the following flow:
    // - invoke `handleSwitchToFullSite`
    // - In `handleSwitchToFullSite`, set viewport and navigate to '/'
    // - invoke `handleSwitchToFullSite`
    // HACK `debounce` to smooth out the calls
    // PROPER Fullsite should be a state in app, instead of reading from cookie
    this.handleSwitchToFullSite = debounce(this.handleSwitchToFullSite, 500);
  }

  render() {
    const {
      data,
      location,
      children,
      enableMwebFullSiteSwitch,
      switchViewFtueComplete,
      welcomeModalFtueComplete,
    } = this.props;
    const { pathname } = location;
    const { moka, notification, subscriptionExpired } = data;
    const { loggedIn, user, permissions } = moka;

    let isRegistrationComplete;
    if (user) {
      const { business } = user;
      isRegistrationComplete = business.is_registration_complete;
      if (isRegistrationComplete === null) {
        isRegistrationComplete = true;
      }
    }

    const subMenuOf = _.groupBy(permissions, permission =>
      permission.menu.toLowerCase()
    );

    const menuMap = {
      '/reports': 'reports',
      '/settings': 'account settings',
      '/library': 'library',
      '/customers': 'customers',
      '/employees': 'employees',
      '/outlets': 'outlets',
      '/inventory': 'inventory',
      '/ingredient': 'ingredient',
    };
    const subMenuKey = find(menuMap, (value, key) => pathname.startsWith(key));
    let subNav;
    if (subMenuKey) {
      subNav = (
        <SubNav
          links={this._toLinks(subMenuOf[subMenuKey])}
          location={location}
          user={user}
          subscriptionExpired={subscriptionExpired}
        />
      );
    }

    const showSwitchBanner =
      enableMwebFullSiteSwitch &&
      loggedIn &&
      !pathname.startsWith('/business-info') &&
      !pathname.startsWith('/kyb');
    const displaySwitchFtue = showSwitchBanner && !switchViewFtueComplete;

    return (
      <div className="full-height">
        {showSwitchBanner && (
          <MwebSwitchBanner onClickSwitchButton={this.handleSwitchToFullSite} />
        )}
        <ProgressBar updateTime={500} currentPath={pathname} />
        <header className={`mk-head-bar ${showSwitchBanner && styles.header}`}>
          <Nav loggedIn={loggedIn} user={user} location={location} />

          {subNav}

          <NotificationBar notification={notification} location={location} />
        </header>
        <div className="separator" />
        <div
          className={classNames(
            'full-height',
            'section-content',
            `${this._getClassByPath()}`,
            showSwitchBanner && styles.contentWithBanner
          )}
        >
          <div className="visible-sm visible-xs reminder-responsive">
            <BillingReminder
              user={user}
              subscriptionExpired={subscriptionExpired}
            />
          </div>
          <div className="container-full-nosub box-content-container not-logged-in">
            {children}
          </div>
        </div>

        {displaySwitchFtue && (
          <MwebFullSiteSwitchFtue
            onViewFullSiteButtonClick={this.handleSwitchToFullSite}
            isWelcomeFtueComplete={welcomeModalFtueComplete}
            onCompletionWelcomeModal={this.handleCompletionWelcomeModal}
            onCompletion={this.handleCompletionSwitchViewsFtue}
          />
        )}
      </div>
    );
  }

  _getClassByPath() {
    const { location } = this.props;
    const classbyPath = location.pathname.split('/');
    return classbyPath[classbyPath.length - 1];
  }

  _toLinks = subMenus => {
    const isMobile = window.innerWidth <= 768;
    const links = [];
    const sortedSubMenu = _.orderBy(subMenus, 'priority_rank');
    for (let i = 0; i < sortedSubMenu.length; i++) {
      const subMenu = sortedSubMenu[i];
      if (isMobile && !subMenu.mobile_supported) {
        continue;
      }
      links.push({
        href: subMenu.urls[0],
        label: subMenu.sub_menu,
      });
    }
    return links;
  };

  handleSwitchToFullSite = () => {
    // Disable responsive view
    setFullSiteMeta();
    // To keep user in full site mode when refreshing page
    setResponsiveMode(false);

    // Redirect to default landing page
    browserHistory.push('/');

    switchToFullSiteClevertap();
  };

  componentDidMount() {
    const { fetchExperiences, enableMwebFullSiteSwitch } = this.props;
    if (enableMwebFullSiteSwitch) {
      fetchExperiences();
    }
  }

  handleCompletionWelcomeModal = () => {
    const { completeExperience } = this.props;
    completeExperience(FTUE_MWEB_WELCOME_MODAL);
  };

  handleCompletionSwitchViewsFtue = () => {
    const { completeExperience } = this.props;
    completeExperience(FTUE_MWEB_FULL_SITE_SWITCH);
  };
}

function mapStateToProps(state) {
  return {
    data: {
      moka: state.moka,
      subscriptionExpired: state.subscriptionExpired,
      notification: state.notification,
    },
    welcomeModalFtueComplete: isExperienceComplete(
      state,
      FTUE_MWEB_WELCOME_MODAL
    ),
    switchViewFtueComplete: isExperienceComplete(
      state,
      FTUE_MWEB_FULL_SITE_SWITCH
    ),
  };
}

const mapDispatchToProps = {
  fetchExperiences,
  completeExperience,
};

const mapFlagsToProps = flags => ({
  enableMwebFullSiteSwitch: flags.enableMwebFullSiteSwitch,
});

export default withFeatureFlags(mapFlagsToProps)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(LayoutNotLoggedIn)
);
